/* =====================================================================
   SNC Cart Drawer — comportements additionnels.
   Tout passe par de la delegation sur document : le contenu du drawer
   est remplace par innerHTML a chaque mise a jour du panier, donc des
   listeners attaches aux noeuds seraient perdus.
   ===================================================================== */

(() => {
  if (window.__sncCartDrawerReady) return;
  window.__sncCartDrawerReady = true;

  const getDrawer = () => document.querySelector('cart-drawer');

  const t = (key, fallback) => {
    const strings = window.sncCartStrings || {};
    return strings[key] || fallback;
  };

  /* ------------------------------------------------------------------
     Re-rendu du drawer a partir d'une reponse /cart/add.js
     ------------------------------------------------------------------ */
  const renderDrawer = (drawer, state) => {
    if (!drawer) return;
    try {
      drawer.renderContents(state);
    } catch (error) {
      console.warn('SNC cart drawer: rendu partiel, rechargement des sections.', error);
      refreshDrawerSections();
    }
  };

  // Filet de secours : on va rechercher la section cart-drawer et on
  // remplace .drawer__inner nous-memes.
  const refreshDrawerSections = async () => {
    const drawer = getDrawer();
    if (!drawer) return;

    try {
      const html = await fetch(`${window.location.pathname}?section_id=cart-drawer`).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('.drawer__inner');
      const current = drawer.querySelector('.drawer__inner');
      if (fresh && current) current.innerHTML = fresh.innerHTML;

      const bubbleHtml = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`).then((r) => r.text());
      const bubbleTarget = document.getElementById('cart-icon-bubble');
      const freshBubble = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
      if (bubbleTarget && freshBubble) bubbleTarget.innerHTML = freshBubble.innerHTML;
    } catch (error) {
      console.warn('SNC cart drawer: rafraichissement impossible.', error);
    }
  };

  /* ------------------------------------------------------------------
     1. Upsell : ajout au panier sans quitter le drawer
     ------------------------------------------------------------------ */
  const addUpsell = async (button) => {
    const card = button.closest('.snc-upsell-card');
    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-disabled') === 'true') return;

    const drawer = getDrawer();
    const originalLabel = button.textContent;

    button.setAttribute('aria-disabled', 'true');
    if (card) card.classList.add('is-loading');
    button.textContent = t('adding', 'Dodawanie...');

    const body = {
      items: [{ id: Number(variantId), quantity: Number(button.dataset.quantity || 1) }],
    };

    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.sections = drawer.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const state = await response.json();

      if (state.status) {
        // Erreur metier renvoyee par Shopify (rupture, limite de quantite...)
        button.textContent = state.description || state.message || t('error', 'Blad');
        setTimeout(() => {
          button.textContent = originalLabel;
          button.removeAttribute('aria-disabled');
          if (card) card.classList.remove('is-loading');
        }, 2500);
        return;
      }

      if (Array.isArray(state.items) && !state.id) state.id = state.items[0]?.id;

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'snc-cart-drawer-upsell',
          cartData: state,
        });
      }

      renderDrawer(drawer, state);
    } catch (error) {
      console.error('SNC cart drawer: ajout upsell echoue.', error);
      button.textContent = originalLabel;
      button.removeAttribute('aria-disabled');
      if (card) card.classList.remove('is-loading');
    }
  };

  /* ------------------------------------------------------------------
     2. Code promo
     ------------------------------------------------------------------ */
  const applyDiscount = async (form) => {
    const input = form.querySelector('[data-snc-discount-input]');
    const feedback = form.parentElement?.querySelector('[data-snc-discount-feedback]');
    const submit = form.querySelector('[type="submit"]');
    const code = (input?.value || '').trim();

    if (!code) return;

    const say = (message, state) => {
      if (!feedback) return;
      feedback.textContent = message;
      feedback.dataset.state = state || '';
    };

    submit?.setAttribute('aria-disabled', 'true');
    say(t('discountChecking', 'Sprawdzanie...'), '');

    try {
      // /discount/CODE pose le cookie de reduction cote Shopify.
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
      });

      const cart = await fetch('/cart.js').then((r) => r.json());
      const applied = (cart.cart_level_discount_applications || []).length > 0 || (cart.discount_codes || []).some((d) => d.applicable);

      if (applied) {
        say(t('discountApplied', 'Kod rabatowy zostal zastosowany.'), 'success');
        input.value = '';
        await refreshDrawerSections();
      } else {
        say(t('discountInvalid', 'Ten kod jest nieprawidlowy lub nie dotyczy Twojego koszyka.'), 'error');
      }
    } catch (error) {
      console.warn('SNC cart drawer: code promo.', error);
      say(t('discountInvalid', 'Ten kod jest nieprawidlowy lub nie dotyczy Twojego koszyka.'), 'error');
    } finally {
      submit?.removeAttribute('aria-disabled');
    }
  };

  /* ------------------------------------------------------------------
     3. Panneaux note / code promo (accordeon a deux entrees)
     ------------------------------------------------------------------ */
  const togglePanel = (button) => {
    const name = button.dataset.sncToggle;
    const root = button.closest('.snc-drawer-actions');
    if (!root) return;

    const isOpen = button.getAttribute('aria-expanded') === 'true';

    root.querySelectorAll('[data-snc-toggle]').forEach((other) => {
      other.setAttribute('aria-expanded', 'false');
    });
    root.querySelectorAll('[data-snc-panel]').forEach((panel) => {
      panel.hidden = true;
    });

    if (isOpen) return;

    button.setAttribute('aria-expanded', 'true');
    const panel = root.querySelector(`[data-snc-panel="${name}"]`);
    if (panel) {
      panel.hidden = false;
      panel.querySelector('input, textarea')?.focus();
    }
  };

  /* ------------------------------------------------------------------
     Delegation
     ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const upsellButton = event.target.closest('[data-snc-upsell-add]');
    if (upsellButton) {
      event.preventDefault();
      addUpsell(upsellButton);
      return;
    }

    const toggle = event.target.closest('[data-snc-toggle]');
    if (toggle) {
      event.preventDefault();
      togglePanel(toggle);
      return;
    }

    const continueButton = event.target.closest('[data-snc-continue]');
    if (continueButton) {
      event.preventDefault();
      getDrawer()?.close();
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-snc-discount-form]');
    if (!form) return;
    event.preventDefault();
    applyDiscount(form);
  });

  // Entree dans le champ code promo sans passer par le bouton
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target.closest('[data-snc-discount-input]');
    if (!input) return;
    event.preventDefault();
    const form = input.closest('[data-snc-discount-form]');
    if (form) applyDiscount(form);
  });
})();